import React, { useRef, useEffect, useState, useCallback } from 'react';

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const CANVAS_SIZE = GRID_SIZE * CELL_SIZE;

let gameLoop = null;

export default function Snake() {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('START');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('snakeHighScore');
    return saved ? parseInt(saved) : 0;
  });

  const gameRef = useRef({
    snake: [{ x: 10, y: 10 }],
    direction: { x: 1, y: 0 },
    nextDirection: { x: 1, y: 0 },
    food: { x: 15, y: 10 },
    speed: 150,
    particles: [],
    score: 0,
  });

  const createParticle = useCallback((x, y) => {
    const particles = [];
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      particles.push({
        x: x * CELL_SIZE + CELL_SIZE / 2,
        y: y * CELL_SIZE + CELL_SIZE / 2,
        vx: Math.cos(angle) * (2 + Math.random() * 2),
        vy: Math.sin(angle) * (2 + Math.random() * 2),
        life: 1,
      });
    }
    return particles;
  }, []);

  const spawnFood = useCallback(() => {
    const game = gameRef.current;
    let newFood;
    let valid = false;
    while (!valid) {
      const candidate = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE),
      };
      if (!game.snake.some(seg => seg.x === candidate.x && seg.y === candidate.y)) {
        newFood = candidate;
        valid = true;
      }
    }
    game.food = newFood;
  }, []);

  const resetGame = useCallback(() => {
    const game = gameRef.current;
    game.snake = [{ x: 10, y: 10 }];
    game.direction = { x: 1, y: 0 };
    game.nextDirection = { x: 1, y: 0 };
    game.speed = 150;
    game.score = 0;
    game.particles = [];
    spawnFood();
    setScore(0);
  }, [spawnFood]);

  const drawGame = useCallback((ctx) => {
    const game = gameRef.current;
    
    ctx.fillStyle = '#0a0e27';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.strokeStyle = 'rgba(0,217,255,0.05)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= GRID_SIZE; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_SIZE, 0);
      ctx.lineTo(i * CELL_SIZE, CANVAS_SIZE);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * CELL_SIZE);
      ctx.lineTo(CANVAS_SIZE, i * CELL_SIZE);
      ctx.stroke();
    }

    game.snake.forEach((seg, index) => {
      const isHead = index === 0;
      ctx.fillStyle = isHead ? '#40e0ff' : '#00d9ff';
      ctx.shadowColor = '#00d9ff';
      ctx.shadowBlur = isHead ? 15 : 8;
      ctx.fillRect(seg.x * CELL_SIZE + 1, seg.y * CELL_SIZE + 1, CELL_SIZE - 2, CELL_SIZE - 2);
    });
    ctx.shadowBlur = 0;

    const pulse = Math.sin(Date.now() / 150) * 0.3 + 0.7;
    ctx.fillStyle = '#ff006e';
    ctx.shadowColor = '#ff006e';
    ctx.shadowBlur = 15 * pulse;
    ctx.fillRect(game.food.x * CELL_SIZE + 2, game.food.y * CELL_SIZE + 2, CELL_SIZE - 4, CELL_SIZE - 4);
    ctx.shadowBlur = 0;

    game.particles = game.particles.filter(p => {
      ctx.fillStyle = `rgba(255, 0, 110, ${p.life})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3 * p.life, 0, Math.PI * 2);
      ctx.fill();
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 0.03;
      return p.life > 0;
    });
  }, []);

  const updateGame = useCallback(() => {
    const game = gameRef.current;
    game.direction = { ...game.nextDirection };

    const head = { 
      x: game.snake[0].x + game.direction.x, 
      y: game.snake[0].y + game.direction.y 
    };

    if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
      setGameState('GAME_OVER');
      if (game.score > highScore) {
        setHighScore(game.score);
        localStorage.setItem('snakeHighScore', game.score.toString());
      }
      return;
    }

    if (game.snake.some(seg => seg.x === head.x && seg.y === head.y)) {
      setGameState('GAME_OVER');
      if (game.score > highScore) {
        setHighScore(game.score);
        localStorage.setItem('snakeHighScore', game.score.toString());
      }
      return;
    }

    game.snake.unshift(head);

    if (head.x === game.food.x && head.y === game.food.y) {
      game.score += 1;
      setScore(game.score);
      if (game.score % 5 === 0 && game.speed > 80) {
        game.speed -= 10;
      }
      game.particles = [...game.particles, ...createParticle(game.food.x, game.food.y)];
      spawnFood();
    } else {
      game.snake.pop();
    }
  }, [highScore, createParticle, spawnFood]);

  const gameLoopFn = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    updateGame();
    drawGame(ctx);
  }, [updateGame, drawGame]);

  useEffect(() => {
    if (gameState === 'PLAYING') {
      resetGame();
      gameLoop = setInterval(gameLoopFn, gameRef.current.speed);
    } else {
      if (gameLoop) {
        clearInterval(gameLoop);
        gameLoop = null;
      }
      if (gameState === 'START') {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#0a0e27';
          ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          
          ctx.strokeStyle = 'rgba(0,217,255,0.05)';
          ctx.lineWidth = 1;
          for (let i = 0; i <= GRID_SIZE; i++) {
            ctx.beginPath();
            ctx.moveTo(i * CELL_SIZE, 0);
            ctx.lineTo(i * CELL_SIZE, CANVAS_SIZE);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, i * CELL_SIZE);
            ctx.lineTo(CANVAS_SIZE, i * CELL_SIZE);
            ctx.stroke();
          }
        }
      }
    }
    return () => {
      if (gameLoop) {
        clearInterval(gameLoop);
        gameLoop = null;
      }
    };
  }, [gameState, gameLoopFn, resetGame]);

  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;

    const handleKeyDown = (e) => {
      if (gameState === 'START') {
        setGameState('PLAYING');
        return;
      }
      
      if (gameState === 'GAME_OVER') {
        if (e.key === 'Enter' || e.key === ' ') {
          setGameState('START');
        }
        return;
      }

      const key = e.key.toLowerCase();
      const game = gameRef.current;
      
      if ((key === 'arrowup' || key === 'w') && game.direction.y !== 1) {
        game.nextDirection = { x: 0, y: -1 };
      } else if ((key === 'arrowdown' || key === 's') && game.direction.y !== -1) {
        game.nextDirection = { x: 0, y: 1 };
      } else if ((key === 'arrowleft' || key === 'a') && game.direction.x !== 1) {
        game.nextDirection = { x: -1, y: 0 };
      } else if ((key === 'arrowright' || key === 'd') && game.direction.x !== -1) {
        game.nextDirection = { x: 1, y: 0 };
      }
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (gameState === 'START') {
        setGameState('PLAYING');
        return;
      }
      if (gameState === 'GAME_OVER') {
        return;
      }
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (gameState !== 'PLAYING') return;

      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;
      const dx = touchEndX - touchStartX;
      const dy = touchEndY - touchStartY;
      
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      
      const game = gameRef.current;
      
      if (Math.abs(dx) > Math.abs(dy)) {
        if (dx > 20 && game.direction.x !== -1) {
          game.nextDirection = { x: 1, y: 0 };
        } else if (dx < -20 && game.direction.x !== 1) {
          game.nextDirection = { x: -1, y: 0 };
        }
      } else {
        if (dy > 20 && game.direction.y !== -1) {
          game.nextDirection = { x: 0, y: 1 };
        } else if (dy < -20 && game.direction.y !== 1) {
          game.nextDirection = { x: 0, y: -1 };
        }
      }

      touchStartX = touchEndX;
      touchStartY = touchEndY;
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const canvas = canvasRef.current;
    window.addEventListener('keydown', handleKeyDown);
    canvas?.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas?.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas?.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas?.removeEventListener('touchstart', handleTouchStart);
      canvas?.removeEventListener('touchmove', handleTouchMove);
      canvas?.removeEventListener('touchend', handleTouchEnd);
    };
  }, [gameState]);

  return (
    <div className="flex flex-col items-center" style={{ maxWidth: '400px', margin: '0 auto', width: '100%', padding: '0.5rem' }}>
      <div className="flex justify-between w-full mb-3" style={{ gap: '1rem' }}>
        <div style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: 'clamp(14px, 4vw, 24px)', fontWeight: 'bold', textShadow: '0 0 10px #00d9ff' }}>
          Score: {score}
        </div>
        <div style={{ color: '#ff006e', fontFamily: 'monospace', fontSize: 'clamp(12px, 3.5vw, 18px)', textShadow: '0 0 10px #ff006e' }}>
          Best: {highScore}
        </div>
      </div>

      <div className="relative" style={{ width: '100%', maxWidth: '400px', aspectRatio: '1/1' }}>
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          className="rounded-lg"
          style={{ 
            border: '2px solid rgba(0,217,255,0.3)',
            boxShadow: '0 0 20px rgba(0,217,255,0.2)',
            touchAction: 'none',
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />
        
        {gameState === 'START' && (
          <div 
            className="absolute inset-0 flex flex-col items-center justify-center rounded-lg"
            style={{ 
              background: 'rgba(10,14,39,0.9)',
              border: '1px solid #00d9ff',
              boxShadow: '0 0 30px rgba(0,217,255,0.3)',
              touchAction: 'none',
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setGameState('PLAYING');
            }}
            onClick={() => setGameState('PLAYING')}
          >
            <p style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: 'clamp(14px, 5vw, 20px)', textShadow: '0 0 15px #00d9ff' }}>
              Tap or Press any key to start
            </p>
          </div>
        )}

        {gameState === 'GAME_OVER' && (
          <div 
            className="absolute inset-0 flex flex-col items-center justify-center rounded-lg"
            style={{ 
              background: 'rgba(10,14,39,0.9)',
              border: '1px solid #00d9ff',
              boxShadow: '0 0 30px rgba(0,217,255,0.3)',
              touchAction: 'none',
            }}
          >
            <p style={{ color: '#ff006e', fontFamily: 'monospace', fontSize: 'clamp(20px, 6vw, 28px)', fontWeight: 'bold', textShadow: '0 0 20px #ff006e', marginBottom: '10px' }}>
              GAME OVER
            </p>
            <p style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: 'clamp(14px, 4vw, 18px)', textShadow: '0 0 10px #00d9ff', marginBottom: '5px' }}>
              Score: {score}
            </p>
            <p style={{ color: '#ff006e', fontFamily: 'monospace', fontSize: 'clamp(12px, 3.5vw, 16px)', textShadow: '0 0 10px #ff006e', marginBottom: '20px' }}>
              Best: {highScore}
            </p>
            <button
              onClick={() => setGameState('START')}
              onTouchStart={(e) => {
                e.stopPropagation();
                setGameState('START');
              }}
              style={{
                background: 'transparent',
                border: '2px solid #00d9ff',
                color: '#00d9ff',
                padding: '10px 24px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                cursor: 'pointer',
                boxShadow: '0 0 15px rgba(0,217,255,0.4)',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,217,255,0.1)';
                e.target.style.boxShadow = '0 0 25px rgba(0,217,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.boxShadow = '0 0 15px rgba(0,217,255,0.4)';
              }}
            >
              Play Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}